import { create } from "zustand";
import { persist } from "zustand/middleware";
import i18n, { type UiLanguage } from "@/i18n";

interface UiState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  reviewViewMode: "gallery" | "kanban";
  setReviewViewMode: (mode: "gallery" | "kanban") => void;
  /** Command palette (⌘K). Transient — kept out of partialize. */
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  /** UI display language. Persisted. */
  language: UiLanguage;
  setLanguage: (l: UiLanguage) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      reviewViewMode: "gallery",
      setReviewViewMode: (mode) => set({ reviewViewMode: mode }),
      paletteOpen: false,
      setPaletteOpen: (v) => set({ paletteOpen: v }),
      language: (i18n.language?.slice(0, 2) as UiLanguage) || "en",
      setLanguage: (l) => {
        i18n.changeLanguage(l);
        set({ language: l });
      },
    }),
    {
      name: "content-os:ui",
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        reviewViewMode: s.reviewViewMode,
        language: s.language,
      }),
      // After rehydration, sync persisted language back into i18n
      onRehydrateStorage: () => (state) => {
        if (state?.language) {
          i18n.changeLanguage(state.language);
        }
      },
    }
  )
);
